import {isTruthy} from "@khanacademy/wonder-stuff-core";

import type {
    SetOfStandards,
    Standard,
    StandardMappedContentItem,
} from "../types.ts";

const MAX_TREE_DEPTH = 10;

// We need to intuit a tree from the flat data.  We'll use a couple of rules:
// 1. If a standard has no children, it's a leaf node.
// 2. If a standard has children, it's a branch node.
// 3. If a standard has no parent (is not a child of another standard), it's a root node.
export function transformSetOfStandardsToTree(
    setOfStandards: SetOfStandards,
    options?: {
        // This is a development flag: useful to confirm things look reasonable
        // before mappings are imported.
        includeUnmapped: boolean;
    },
): Array<Standard> {
    if (!setOfStandards) {
        return [];
    }

    const {id: setId, standards} = setOfStandards;

    if (!setId || !standards) {
        return [];
    }

    const allStandards = standards.filter(isTruthy);
    const standardsMap = new Map(allStandards.map((s) => [s.standardId, s]));

    // start by finding root nodes.  They will be the standards that are not
    // found in the children of any other standard
    const rootNodes = findRootStandards(allStandards);

    // Now recursively populate the tree
    const results = rootNodes
        .map((rootNode) => {
            if (!rootNode) {
                return null;
            }

            return populateStandard(rootNode, standardsMap, setId);
        })
        .filter(isTruthy);

    if (options?.includeUnmapped) {
        return results;
    }
    return results.filter(hasMappedContentIncludingDescendents);
}

const isLearnableContent = (item: StandardMappedContentItem): boolean =>
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    ["Article", "Exercise", "Video"].includes(item.__typename);

const hasMappedContentIncludingDescendents = (standard: Standard): boolean =>
    (standard.mappedContent || []).filter(isLearnableContent).length > 0 ||
    (standard.standards || []).reduce(
        (oldHasMapped, standard) =>
            oldHasMapped || hasMappedContentIncludingDescendents(standard),
        false,
    );

export const findRootStandards = (
    standards?:
        | Array<Standard | null | undefined>
        | null
        | undefined
        | Array<Standard>
        | null
        | undefined,
): Array<Standard> => {
    if (!standards) {
        return [];
    }
    const allStandards = standards.filter(isTruthy);
    // @ts-expect-error - TS2322 - Type '(Standard | null | undefined)[]' is not assignable to type 'Standard[]'.
    return allStandards.filter((standard) =>
        // @ts-expect-error - TS2345 - Argument of type 'Standard | null | undefined' is not assignable to parameter of type 'Standard'.
        standardHasNoParent(standard, allStandards),
    );
};

export function getStandardSubTreeFromSet(
    standardId: string,
    standardsSubTrees: Array<Standard>,
): Standard | null | undefined {
    return standardsSubTrees
        .map((t) => subTreeRootedAtStandardId(standardId, t))
        .find((t) => t !== undefined);
}

export function subTreeRootedAtStandardId(
    standardId: string,
    standardTree: Standard,
): Standard | null | undefined {
    // Found the ID
    if (standardTree.standardId === standardId) {
        return standardTree;
    }

    // Didn't find the ID and reached a leaf
    if (!standardTree.standards?.length) {
        // undefined will match the "not found" case in the find below
        return undefined;
    }

    // Didn't find the ID, but have children to search
    return (standardTree.standards ?? [])
        .map((t) => subTreeRootedAtStandardId(standardId, t))
        .find((t) => t !== undefined);
}

export function isTopLevelStandard(
    standardId: string,
    setTrees: Array<Standard>,
): boolean {
    return setTrees.some((s) => s.standardId === standardId);
}

function standardHasNoParent(
    standard: Standard,
    allStandards: Array<Standard>,
): boolean {
    return allStandards.every((otherStandard) => {
        return !(otherStandard.children ?? []).includes(standard.standardId);
    });
}

function populateStandard(
    standard: Standard,
    allStandards: Map<string | null | undefined, Standard>,
    setId: string,
    parentStandard?: Standard | null,
    depth = 0,
): Standard | null | undefined {
    const {
        standardId,
        description,
        coverage,
        children,
        mappedContent,
        relativeUrl,
        groupLabel,
    } = standard;

    let childStandards: Array<Standard | null | undefined> = [];

    if (depth < MAX_TREE_DEPTH) {
        childStandards = (children ?? [])
            .map((childId) => {
                if (!childId) {
                    return null;
                }

                const childStandard = allStandards.get(childId);
                if (!childStandard) {
                    return null;
                }

                return populateStandard(
                    childStandard,
                    allStandards,
                    setId,
                    standard,
                    depth + 1,
                );
            })
            .filter(isTruthy);
    }

    const childrenWithoutNull: Array<Standard> =
        childStandards.filter(isTruthy);
    const parentStandardDescription = parentStandard?.description ?? "";
    const mappedContentResult = (mappedContent ?? [])
        .map((mappedContentChild) => {
            if (
                mappedContentChild &&
                mappedContentChild.id &&
                mappedContentChild.title &&
                mappedContentChild.defaultUrlPath &&
                mappedContentChild.contentKind
            ) {
                return {
                    __typename: mappedContentChild.__typename,
                    id: mappedContentChild.id,
                    contentKind: mappedContentChild.contentKind,
                    title: mappedContentChild.title,
                    defaultUrlPath: mappedContentChild.defaultUrlPath,
                    urlWithinStandardSet:
                        mappedContentChild.urlWithinStandardSet,
                };
            }
        })
        .filter(isTruthy)
        .sort((a: StandardMappedContentItem, b: StandardMappedContentItem) => {
            return a.title.localeCompare(b.title) || sortByContentKind(a, b);
        });

    if (!standardId) {
        return null;
    }

    return {
        setId,
        standardId: standardId,
        description: description ?? "",
        coverage,
        standards: childrenWithoutNull,
        relativeUrl,
        mappedContent: mappedContentResult,
        parentStandardDescription: parentStandardDescription,
        groupLabel,
    };
}

// Sort the related items by their content kind (custom hierarchy is video, article, then exercise)
function sortByContentKind(
    a: StandardMappedContentItem,
    b: StandardMappedContentItem,
): number {
    switch (a.contentKind) {
        case "Video":
            if (b.contentKind === "Video") {
                return 0;
            }
            if (b.contentKind === "Article" || b.contentKind === "Exercise") {
                return -1;
            }
            break;
        case "Article":
            if (b.contentKind === "Video") {
                return 1;
            }
            if (b.contentKind === "Article") {
                return 0;
            }
            if (b.contentKind === "Exercise") {
                return -1;
            }
            break;
        case "Exercise":
            if (b.contentKind === "Video" || b.contentKind === "Article") {
                return 1;
            }
            if (b.contentKind === "Exercise") {
                return 0;
            }
            break;
        default:
            return 1;
    }

    return 1;
}
